'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@terra/ui-styled-system';
import {
  computeLineRange,
  getSelectedTextWithin,
  getSelectionToolbarContext,
  type SelectionToolbarContext,
} from './objectContextSelectionUtils';
import { SendToChatIconButton } from '../SendToChatIconButton';

const SELECTION_ACTION_DELAY_MS = 300;

type ObjectContextSelectionToolbarProps = {
  field: string;
  fieldLabel: string;
  fullText: string;
  sendToChatLabel: string;
  onSendSelection: ({
    field,
    fieldLabel,
    excerpt,
    lineStart,
    lineEnd,
  }: {
    field: string;
    fieldLabel: string;
    excerpt: string;
    lineStart?: number;
    lineEnd?: number;
  }) => void;
};

export function ObjectContextSelectionToolbar({
  field,
  fieldLabel,
  fullText,
  sendToChatLabel,
  onSendSelection,
  children,
}: ObjectContextSelectionToolbarProps & { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const toolbarButtonRef = useRef<HTMLButtonElement>(null);
  const selectedTextRef = useRef('');
  const pointerPositionRef = useRef<{ x: number; y: number } | null>(null);
  const selectionActionTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [selectionAction, setSelectionAction] =
    useState<SelectionToolbarContext | null>(null);

  const clearSelectionAction = useCallback(() => {
    selectedTextRef.current = '';
    setSelectionAction(null);
  }, []);

  const clearSelectionActionTimeout = useCallback(() => {
    if (!selectionActionTimeoutRef.current) {
      return;
    }

    clearTimeout(selectionActionTimeoutRef.current);
    selectionActionTimeoutRef.current = null;
  }, []);

  const showSelectionAction = useCallback(
    (fallbackPosition?: { x: number; y: number }) => {
      const selectionContext = getSelectionToolbarContext({
        container: containerRef.current,
        fallbackPosition,
      });

      selectedTextRef.current = selectionContext?.selectedText ?? '';
      if (!selectionContext) {
        clearSelectionAction();

        return;
      }

      setSelectionAction(selectionContext);
    },
    [clearSelectionAction],
  );

  const scheduleSelectionAction = useCallback(
    (fallbackPosition?: { x: number; y: number }) => {
      clearSelectionActionTimeout();
      selectionActionTimeoutRef.current = setTimeout(() => {
        showSelectionAction(fallbackPosition);
        selectionActionTimeoutRef.current = null;
      }, SELECTION_ACTION_DELAY_MS);
    },
    [clearSelectionActionTimeout, showSelectionAction],
  );

  const scheduleSelectionActionFromCurrentSelection = useCallback(
    (fallbackPosition?: { x: number; y: number }) => {
      window.setTimeout(() => {
        const selectionContext = getSelectionToolbarContext({
          container: containerRef.current,
          fallbackPosition,
        });

        selectedTextRef.current = selectionContext?.selectedText ?? '';
        if (selectionContext) {
          scheduleSelectionAction(fallbackPosition);

          return;
        }

        clearSelectionActionTimeout();
        clearSelectionAction();
      }, 0);
    },
    [
      clearSelectionAction,
      clearSelectionActionTimeout,
      scheduleSelectionAction,
    ],
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      pointerPositionRef.current = { x: event.clientX, y: event.clientY };
      clearSelectionActionTimeout();
      clearSelectionAction();
    },
    [clearSelectionAction, clearSelectionActionTimeout],
  );

  const handleMouseUp = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      pointerPositionRef.current = { x: event.clientX, y: event.clientY };
      scheduleSelectionActionFromCurrentSelection(pointerPositionRef.current);
    },
    [scheduleSelectionActionFromCurrentSelection],
  );

  const handleSendSelection = useCallback(() => {
    const excerpt =
      getSelectedTextWithin(containerRef.current) || selectedTextRef.current;

    if (!excerpt) {
      return;
    }

    const lineRange = computeLineRange({
      fullText,
      excerpt,
    });

    onSendSelection({
      field,
      fieldLabel,
      excerpt,
      lineStart: lineRange?.lineStart,
      lineEnd: lineRange?.lineEnd,
    });
    clearSelectionAction();
    window.getSelection()?.removeAllRanges();
  }, [clearSelectionAction, field, fieldLabel, fullText, onSendSelection]);

  useEffect(() => {
    return () => clearSelectionActionTimeout();
  }, [clearSelectionActionTimeout]);

  useEffect(() => {
    const handleDocumentSelection = (event: Event) => {
      const fallbackPosition =
        event instanceof globalThis.MouseEvent
          ? { x: event.clientX, y: event.clientY }
          : (pointerPositionRef.current ?? undefined);

      if (fallbackPosition) {
        pointerPositionRef.current = fallbackPosition;
      }

      scheduleSelectionActionFromCurrentSelection(fallbackPosition);
    };

    document.addEventListener('selectionchange', handleDocumentSelection);
    document.addEventListener('mouseup', handleDocumentSelection);
    document.addEventListener('keyup', handleDocumentSelection);

    return () => {
      document.removeEventListener('selectionchange', handleDocumentSelection);
      document.removeEventListener('mouseup', handleDocumentSelection);
      document.removeEventListener('keyup', handleDocumentSelection);
    };
  }, [scheduleSelectionActionFromCurrentSelection]);

  useEffect(() => {
    if (!selectionAction) {
      return;
    }

    const closeSelectionAction = (event: MouseEvent) => {
      const target = event.target;

      if (
        target instanceof Node &&
        toolbarButtonRef.current?.contains(target)
      ) {
        return;
      }

      clearSelectionAction();
    };

    const timeout = window.setTimeout(() => {
      document.addEventListener('click', closeSelectionAction);
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearSelectionAction();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener('click', closeSelectionAction);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [clearSelectionAction, selectionAction]);

  return (
    <Box position="relative" ref={containerRef} w="full">
      <Box onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
        {children}
      </Box>
      {selectionAction ? (
        <Box
          position="fixed"
          left={`${selectionAction.x}px`}
          top={`${selectionAction.y}px`}
          zIndex={9999}
        >
          <SendToChatIconButton
            label={sendToChatLabel}
            buttonRef={toolbarButtonRef}
            variant="outline"
            iconSize={14}
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleSendSelection}
          />
        </Box>
      ) : null}
    </Box>
  );
}
